import { FormEvent, useEffect, useState } from "react"
import { Link, useNavigate } from "react-router-dom"
import { changePassword, getProfile } from "~/api/auth"
import { LoggedInScreenCard } from "~/components/LoggedInScreenCard"
import { PageShell } from "~/components/PageShell"

export function ChangePasswordPage() {
  const navigate = useNavigate()
  const [currentPassword, setCurrentPassword] = useState("")
  const [password, setPassword] = useState("")
  const [passwordConfirmation, setPasswordConfirmation] = useState("")
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    getProfile().catch(() => navigate("/login", { replace: true }))
  }, [navigate])

  async function handleSubmit(event: FormEvent) {
    event.preventDefault()
    setError(null)
    setSuccess(null)
    setSaving(true)
    try {
      await changePassword(currentPassword, password, passwordConfirmation)
      setSuccess("Password updated.")
      setCurrentPassword("")
      setPassword("")
      setPasswordConfirmation("")
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not change password")
    } finally {
      setSaving(false)
    }
  }

  return (
    <PageShell innerClassName="max-w-lg w-full" fixedTabNav>
      <LoggedInScreenCard>
        <div className="w-full p-2">
          <h1 className="text-2xl font-semibold text-[var(--color-text-primary)]">Change password</h1>
          <p className="mt-2 text-sm text-[var(--color-text-secondary)]">
            Enter your current password and choose a new one.
          </p>

          <form onSubmit={handleSubmit} className="mt-6 space-y-4">
            <label className="block text-sm text-[var(--color-text-primary)]">
              Current password
              <input
                type="password"
                autoComplete="current-password"
                aria-label="Current password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                required
                className="mt-1 w-full rounded-lg border border-[var(--color-divider)] bg-[var(--color-surface-card)] px-3 py-2"
              />
            </label>
            <label className="block text-sm text-[var(--color-text-primary)]">
              New password
              <input
                type="password"
                autoComplete="new-password"
                aria-label="New password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="mt-1 w-full rounded-lg border border-[var(--color-divider)] bg-[var(--color-surface-card)] px-3 py-2"
              />
            </label>
            <label className="block text-sm text-[var(--color-text-primary)]">
              Confirm new password
              <input
                type="password"
                autoComplete="new-password"
                aria-label="Confirm new password"
                value={passwordConfirmation}
                onChange={(e) => setPasswordConfirmation(e.target.value)}
                required
                className="mt-1 w-full rounded-lg border border-[var(--color-divider)] bg-[var(--color-surface-card)] px-3 py-2"
              />
            </label>

            {error && (
              <p className="text-sm text-red-600" role="alert">
                {error}
              </p>
            )}
            {success && (
              <p className="text-sm text-green-700" role="status">
                {success}
              </p>
            )}

            <button
              type="submit"
              disabled={saving}
              className="rounded-lg bg-[var(--color-text-primary)] px-4 py-2 text-sm font-medium text-[var(--color-surface-card)] transition hover:opacity-90 disabled:opacity-50"
            >
              {saving ? "Saving…" : "Save new password"}
            </button>
          </form>

          <Link
            to="/account"
            className="mt-6 inline-block text-sm font-medium text-[var(--color-text-primary)] underline"
          >
            Back to Account Details
          </Link>
        </div>
      </LoggedInScreenCard>
    </PageShell>
  )
}
